'use client'

import { useState, useEffect } from 'react'
import { SubtaskTree } from './SubtaskTree'

// ============================================================================
// Types
// ============================================================================

interface Task {
  id: string
  titulo: string
  status: string
  prioridade: number
  created_at: string
  subtask_count?: number
}

interface TaskCardProps {
  taskId: string
  showSubtasks?: boolean
  className?: string
}

// ============================================================================
// Component
// ============================================================================

export function TaskCard({
  taskId,
  showSubtasks = false,
  className = ''
}: TaskCardProps) {
  const [task, setTask] = useState<Task | null>(null)
  const [loading, setLoading] = useState(true)
  const [showTree, setShowTree] = useState(showSubtasks)

  useEffect(() => {
    fetchTask()
  }, [taskId])

  const fetchTask = async () => {
    try {
      const response = await fetch(`https://api.lldonha.com/api/tasks/${taskId}`)
      const data = await response.json()

      if (data.success) {
        setTask(data.task)
      }
    } catch (error) {
      console.error('Error fetching task:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg h-24 ${className}`} />
    )
  }

  if (!task) {
    return null
  }

  const statusColors = {
    pending: 'bg-gray-100 text-gray-700 border-gray-300',
    running: 'bg-blue-100 text-blue-700 border-blue-300',
    done: 'bg-green-100 text-green-700 border-green-300',
    error: 'bg-red-100 text-red-700 border-red-300',
    needs_review: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    approved: 'bg-green-100 text-green-700 border-green-300'
  }

  const statusIcons = {
    pending: '⏸️',
    running: '🔄',
    done: '✅',
    error: '❌',
    needs_review: '📝',
    approved: '✅'
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 ${className}`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
            {task.titulo}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            ID: {taskId.slice(0, 8)}...
          </p>
        </div>

        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[task.status as keyof typeof statusColors] || statusColors.pending}`}>
          {statusIcons[task.status as keyof typeof statusIcons]} {task.status.toUpperCase()}
        </span>
      </div>

      {/* Metadata */}
      <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 mb-3">
        <span>Prioridade: {task.prioridade}</span>
        <span>•</span>
        <span>Criada: {new Date(task.created_at).toLocaleString('pt-BR')}</span>
        {task.subtask_count !== undefined && task.subtask_count > 0 && (
          <>
            <span>•</span>
            <span>{task.subtask_count} subtasks</span>
          </>
        )}
      </div>

      {/* Subtasks Toggle */}
      {task.subtask_count !== undefined && task.subtask_count > 0 && (
        <button
          onClick={() => setShowTree(!showTree)}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
        >
          {showTree ? '▼' : '▶'} {showTree ? 'Ocultar' : 'Mostrar'} hierarquia
        </button>
      )}

      {/* Subtask Tree */}
      {showTree && (
        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
          <SubtaskTree taskId={taskId} />
        </div>
      )}
    </div>
  )
}
